<template>
  <v-dialog
    v-model="isOpen"
    max-width="600px"
    persistent
    overlay-opacity="0.9"
  >
    <v-card
      class="camera-modal"
      dark
    >
      <v-card-title class="justify-center pa-6">
        <v-icon
          left
          color="white"
          size="24"
        >
          {{ icons.mdiCamera }}
        </v-icon>
        <span class="text-h6 font-weight-bold">Capturar Foto</span>
      </v-card-title>

      <v-card-text class="pa-6">
        <div class="camera-container">
          <video
            ref="video"
            autoplay
            playsinline
            class="camera-video"
          ></video>

          <div
            v-if="isLoading"
            class="loading-overlay"
          >
            <v-progress-circular
              indeterminate
              color="white"
              size="64"
            ></v-progress-circular>
            <span class="loading-text mt-4">Iniciando câmera...</span>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="justify-center pa-6">
        <v-btn
          large
          color="info"
          class="capture-btn mr-4"
          :disabled="isLoading"
          @click="capturePhoto"
        >
          <v-icon left>
            {{ icons.mdiCamera }}
          </v-icon>
          Capturar
        </v-btn>

        <v-btn
          large
          outlined
          color="white"
          @click="closeModal"
        >
          <v-icon left>
            {{ icons.mdiClose }}
          </v-icon>
          Fechar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mdiCamera, mdiCameraIris, mdiClose } from '@mdi/js'

export default {
  name: 'CameraModal',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      icons: {
        mdiCamera,
        mdiCameraIris,
        mdiClose,
      },
      stream: null,
      isLoading: true,
    }
  },
  computed: {
    isOpen: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },
  },
  watch: {
    value: {
      handler(newVal) {
        if (newVal) {
          this.initCamera()
        } else {
          this.cleanup()
        }
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    this.cleanup()
  },
  methods: {
    async initCamera() {
      try {
        this.isLoading = true

        // Verifica se há câmeras disponíveis
        const devices = await navigator.mediaDevices.enumerateDevices()
        const videoDevices = devices.filter(device => device.kind === 'videoinput')

        if (videoDevices.length === 0) {
          this.$emit('error', 'Nenhuma câmera detectada. Verifique se a câmera USB está conectada e funcionando.')
          this.closeModal()

          return
        }

        // Configurações da câmera
        const constraints = {
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280, max: 1920 },
            height: { ideal: 720, max: 1080 },
          },
        }

        this.stream = await navigator.mediaDevices.getUserMedia(constraints)

        // Aguarda o próximo tick para garantir que o elemento existe
        await this.$nextTick()

        if (this.$refs.video) {
          this.$refs.video.srcObject = this.stream
          this.isLoading = false
        }
      } catch (error) {
        console.error('Erro ao acessar câmera:', error)
        this.handleCameraError(error)
      }
    },

    handleCameraError(error) {
      let errorMessage = 'Erro ao acessar a câmera'

      if (error.name === 'NotAllowedError') {
        errorMessage = 'Permissão para acessar a câmera foi negada. Clique no ícone da câmera na barra de endereços e permita o acesso.'
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'Nenhuma câmera encontrada. Verifique se a câmera USB está conectada e funcionando.'
      } else if (error.name === 'NotSupportedError') {
        errorMessage = 'Câmera não suportada neste navegador.'
      } else if (error.name === 'NotReadableError') {
        errorMessage = 'Câmera está sendo usada por outro aplicativo. Feche outros programas que possam estar usando a câmera.'
      } else if (error.name === 'OverconstrainedError') {
        errorMessage = 'Configurações da câmera não suportadas. Tentando configuração mais simples...'
        this.tryBasicCamera()

        return
      }

      this.$emit('error', errorMessage)
      this.closeModal()
    },

    async tryBasicCamera() {
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({ video: true })

        await this.$nextTick()

        if (this.$refs.video) {
          this.$refs.video.srcObject = this.stream
          this.isLoading = false
        }
      } catch (error) {
        console.error('Câmera básica também falhou:', error)
        this.$emit('error', 'Não foi possível acessar a câmera mesmo com configurações básicas.')
        this.closeModal()
      }
    },

    capturePhoto() {
      if (!this.$refs.video || !this.stream) return

      const { video } = this.$refs
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight

      const ctx = canvas.getContext('2d')
      ctx.drawImage(video, 0, 0)

      // Converte para blob
      canvas.toBlob(blob => {
        if (blob) {
          const file = new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' })
          this.$emit('photo-captured', file)
          this.closeModal()
        }
      }, 'image/jpeg', 0.8)
    },

    closeModal() {
      this.cleanup()
      this.isOpen = false
    },

    cleanup() {
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop())
        this.stream = null
      }
      this.isLoading = true
    },
  },
}
</script>

<style scoped>
.camera-modal {
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
}

.camera-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  border-radius: 12px;
  overflow: hidden;
  background: #000;
}

.camera-video {
  width: 100%;
  max-width: 500px;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.8);
  color: white;
}

.loading-text {
  color: white;
  font-weight: 500;
}

.capture-btn {
  min-width: 140px;
  height: 48px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.capture-btn:hover {
  box-shadow: 0 6px 16px rgba(76, 175, 80, 0.4);
}

/* Responsividade */
@media (max-width: 600px) {
  .camera-video {
    max-width: 100%;
  }

  .capture-btn {
    min-width: 120px;
    height: 44px;
  }
}
</style>
